import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle2, 
  MapPin, 
  Phone, 
  Clock, 
  FileCheck, 
  Shield, 
  Award,
  Car,
  DollarSign,
  Calendar,
  Star,
  Quote
} from 'lucide-react';

const HomePage = () => {
  const [showMileageCalculator, setShowMileageCalculator] = useState(false);
  const [miles, setMiles] = useState('');
  const [calculatedFee, setCalculatedFee] = useState(null);

  const services = [
    {
      title: "Individual Acknowledgment",
      description: "Official verification that you signed a document voluntarily and understand its contents.",
      icon: FileCheck,
      price: "$10 per signature"
    },
    {
      title: "Individual Jurat",
      description: "Sworn statement confirming you're swearing to the truthfulness of the document's contents.",
      icon: Shield,
      price: "$10 per signature"
    },
    {
      title: "Oath/Affirmation",
      description: "Formal declaration made under penalty of perjury to confirm truthfulness.",
      icon: Award,
      price: "$10 per signature"
    },
    {
      title: "Copy Certification",
      description: "Official certification that a copy is a true and accurate reproduction of the original document.",
      icon: CheckCircle2,
      price: "$10 per signature"
    }
  ];

  const calculateMileage = () => {
    const distance = parseFloat(miles);
    if (isNaN(distance) || distance < 0) {
      setCalculatedFee("Please enter a valid distance");
      return;
    }

    if (distance > 50) {
      setCalculatedFee("Custom quote - please contact us");
      return;
    }

    let fee = 25;
    if (distance <= 10) {
      fee = 25;
    } else if (distance <= 15) {
      fee = 35;
    } else if (distance <= 20) {
      fee = 40;
    } else if (distance <= 30) {
      fee = 55;
    } else if (distance <= 40) {
      fee = 75;
    } else {
      fee = 95;
    }

    setCalculatedFee(`$${fee.toFixed(2)}`);
  };

  const handleBookingClick = () => {
    window.open('https://cheddas-notary-service.square.site/', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Chedda's Notary</h1>
            </div>
            <Button 
              onClick={handleBookingClick}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 transition-all duration-300 hover:shadow-lg"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Book Appointment
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-1 text-sm font-medium">
                Serving Port Saint Lucie, the Treasure Coast & All of Florida
              </Badge>
              <h2 className="text-5xl font-extrabold text-gray-900 leading-tight">
                Professional Mobile Notary Services at Your Doorstep
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Fast, reliable notary services from a Florida Commissioned Notary Public, delivered to your location. 
                Available by appointment across Florida, with travel fees based on distance from Port St. Lucie, FL.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  onClick={handleBookingClick}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                >
                  Schedule Your Appointment
                </Button>
                <Button 
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-bold px-8 py-6 text-lg transition-all duration-300"
                >
                  View Pricing
                </Button>
              </div>

              <div className="flex flex-wrap gap-6 pt-6">
                <div className="flex items-center space-x-2 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-medium">Florida Commissioned Notary Public & Insured</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <Car className="h-5 w-5 text-green-600" />
                  <span className="font-medium">Mobile Service</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <Clock className="h-5 w-5 text-green-600" />
                  <span className="font-medium">Appointments around your schedule, subject to availability</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img 
                  src="https://images.unsplash.com/photo-1562564055-71e051d33c19?w=800&q=80" 
                  alt="Professional notary service" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-2 border-blue-100">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Service Area</p>
                    <p className="text-xl font-bold text-gray-900">Serving All of Florida</p>
                    <p className="text-xs text-gray-500">Travel fees based on distance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 px-4 py-1 text-sm font-medium mb-4">
              Our Services
            </Badge>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Professional Notary Services
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              All services are competitively priced at just $10 per signature
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={index}
                  className="border-2 border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 bg-white"
                >
                  <CardHeader>
                    <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-2 text-blue-600 font-bold text-lg">
                      <DollarSign className="h-5 w-5" />
                      <span>{service.price}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 px-4 py-1 text-sm font-medium mb-4">
              Transparent Pricing
            </Badge>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Simple, Fair Pricing Structure
            </h3>
            <p className="text-xl text-gray-600">
              No hidden fees, just honest pricing for quality service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                  <FileCheck className="h-6 w-6 mr-3 text-blue-600" />
                  Notary Services
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-blue-200">
                  <span className="text-gray-700 font-medium">Per Signature</span>
                  <span className="text-2xl font-bold text-blue-600">$10</span>
                </div>
                <p className="text-sm text-gray-600 pt-2">
                  Applies to all notarization types: acknowledgments, jurats, oaths, and copy certifications
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                  <Car className="h-6 w-6 mr-3 text-blue-600" />
                  Travel Fees
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-gray-700">0-10 miles</span>
                  <span className="text-lg font-bold text-blue-600">$25</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-gray-700">11-15 miles</span>
                  <span className="text-lg font-bold text-blue-600">$35</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-gray-700">16-20 miles</span>
                  <span className="text-lg font-bold text-blue-600">$40</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-gray-700">21-30 miles</span>
                  <span className="text-lg font-bold text-blue-600">$55</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-gray-700">31-40 miles</span>
                  <span className="text-lg font-bold text-blue-600">$75</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-gray-700">41-50 miles</span>
                  <span className="text-lg font-bold text-blue-600">$95</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Over 50 miles</span>
                  <span className="text-lg font-bold text-blue-600">Custom quote</span>
                </div>
                <p className="text-sm text-gray-600 pt-2">
                  Fees are based on one-way mileage from Port St. Lucie, FL.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Mileage Calculator */}
          <Card className="border-2 border-gray-200 shadow-lg">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-2xl font-bold text-gray-900">
                Calculate Your Travel Fee
              </CardTitle>
              <CardDescription>
                Enter the one-way distance from Port St. Lucie, FL to estimate your travel fee
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distance (miles)
                  </label>
                  <input
                    type="number"
                    value={miles}
                    onChange={(e) => setMiles(e.target.value)}
                    placeholder="Enter miles from Port Saint Lucie"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={calculateMileage}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 h-[50px] transition-all duration-300"
                  >
                    Calculate
                  </Button>
                </div>
              </div>
              {calculatedFee && (
                <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <p className="text-center text-lg">
                    <span className="text-gray-700 font-medium">Estimated Travel Fee: </span>
                    <span className="text-3xl font-bold text-blue-600">{calculatedFee}</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-gray-600 font-medium mt-8">
            Confirm your travel quote before booking.
          </p>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-blue-100 text-blue-700 px-4 py-1 text-sm font-medium mb-4">
                Service Coverage
              </Badge>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                Serving Port Saint Lucie, the Treasure Coast & All of Florida
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Professional mobile notary services brought directly to your location. 
                Local and statewide appointments available, with travel fees based on distance.
              </p>

              <div 
                className="bg-blue-600 text-white font-extrabold text-xl tracking-wide text-center px-6 py-4 rounded-xl shadow-lg mb-8"
                data-testid="service-area-banner"
              >
                CHEDDA'S MOBILE NOTARY — I COME TO YOU!
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <MapPin className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Wide Service Coverage</h4>
                    <p className="text-gray-600">Fixed travel fees up to 50 miles, custom quote for longer trips</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <Car className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">We Come to You</h4>
                    <p className="text-gray-600">Home, office, hospital, or any location you prefer</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <Clock className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Appointments Only</h4>
                    <p className="text-gray-600">Schedule your visit at your convenience</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1619418602850-35ad20aa1700?w=800&q=80" 
                  alt="Legal documents and notary services" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 px-4 py-1 text-sm font-medium mb-4">
              Testimonials
            </Badge>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h3>
            <p className="text-xl text-gray-600">
              Trusted by residents and professionals across the Port St. Lucie area
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Maria G.",
                role: "Home Buyer",
                quote: "Chedda's made closing on our home so easy. He arrived right on time at our realtor's office and had everything signed and sealed in minutes."
              },
              {
                name: "James T.",
                role: "Small Business Owner",
                quote: "I needed documents notarized at my shop during business hours. Professional, punctual, and the travel fee was exactly what the calculator quoted."
              },
              {
                name: "Linda R.",
                role: "Healthcare Professional",
                quote: "My father couldn't leave the hospital, so Chedda's came to us. Kind, patient, and thorough — we couldn't have asked for better service."
              }
            ].map((testimonial, index) => (
              <Card 
                key={index} 
                className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="pt-6">
                  <Quote className="h-8 w-8 text-blue-200 mb-4" />
                  <div className="flex space-x-1 mb-4" data-testid={`testimonial-stars-${index}`}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-gray-100 pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Your Documents Notarized?
          </h3>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Book your appointment today and experience professional, reliable mobile notary service 
            delivered right to your doorstep in Port Saint Lucie.
          </p>
          
          <Button 
            onClick={handleBookingClick}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-10 py-7 text-xl transition-all duration-300 hover:shadow-2xl hover:scale-105"
          >
            <Calendar className="h-6 w-6 mr-3" />
            Schedule Your Appointment Now
          </Button>

          <p className="mt-8 text-blue-100 text-sm">
            By appointment only • Serving Port St. Lucie, FL — travel over 50 miles by custom quote
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="h-8 w-8 text-blue-400" />
                <h4 className="text-xl font-bold text-white">Chedda's Notary</h4>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Professional mobile notary services serving Port Saint Lucie and surrounding areas — travel over 50 miles by custom quote.
              </p>
            </div>

            <div>
              <h5 className="text-lg font-bold text-white mb-4">Services</h5>
              <ul className="space-y-2 text-gray-400">
                <li>Individual Acknowledgment</li>
                <li>Individual Jurat</li>
                <li>Oath/Affirmation</li>
                <li>Copy Certification</li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-bold text-white mb-4">Contact</h5>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span className="text-gray-400">Port Saint Lucie, FL</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <a 
                    href="https://cheddas-notary-service.square.site/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Book via Square Site
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span className="text-gray-400">By Appointment Only</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} Chedda's Notary. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
